// Exclusivity inside a multiselect: a group of options that behaves like radio buttons while the
// rest of the selection stays free. Its own module, and not part of `Utils`, so the settings tests
// can import it without pulling in the store and router that `Utils` needs.

/**
 * A checkbox selection in which a group of mutually exclusive options holds at most the one just
 * checked. Checking a group member clears the others; unchecking one, or toggling an option outside
 * the group, changes nothing else.
 *
 * Used by `MultiSelectionDialog` so an exclusive pair behaves like radio buttons inside a
 * multiselect, rather than being corrected after the dialog closes.
 *
 * @param options The options the selection is indexed by.
 * @param selection The selection with the toggle already applied.
 * @param toggledPosition Index of the option the user just toggled.
 * @param exclusiveOptions The options that exclude each other. An empty group changes nothing.
 * @returns The selection with at most one member of the group checked.
 */
export function applyExclusiveOptions<T>(
  options: T[],
  selection: boolean[],
  toggledPosition: number,
  exclusiveOptions: T[]
): boolean[] {
  if (
    !selection[toggledPosition]
    || !exclusiveOptions.includes(options[toggledPosition])
  ) {
    return selection;
  }

  return selection.map((isSelected, index) =>
    index !== toggledPosition && exclusiveOptions.includes(options[index])
      ? false
      : isSelected
  );
}
